import {isDefined} from "@/utils";
import {Speech} from "@/entities/Speech";

export class SpeechContainer {
  private _startLetters: string[] = [];
  private _speechMap: Record<string, Speech[]> = {};
  private _speechesAfterSearchAndFilter: Speech[] = [];
  private readonly _speeches: Speech[] = [];
  private readonly _sortRegex = new RegExp("[A-Z]");

  constructor(entities: Speech[]) {
    const eventsWithOutSubEvents = entities.filter(
      (e) => !isDefined(e.parentId) || e.parentId === "-1"
    );

    eventsWithOutSubEvents.forEach((e) => {
      e.subSpeeches = entities.filter((ev) => ev.parentId === e.id);
    });

    this.createSpeechMap(eventsWithOutSubEvents);
    this._speechesAfterSearchAndFilter = eventsWithOutSubEvents;
    this._speeches = eventsWithOutSubEvents;
    this.createStartLetters(eventsWithOutSubEvents);
  }

  private createStartLetters(entities: Speech[]): void {
    const letterSet = new Set<string>();
    for (const speech of entities) {
      let firstChar = speech.sortOrder[0]
        .toUpperCase()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");
      if (!this._sortRegex.test(firstChar)) {
        firstChar = "#";
      }
      letterSet.add(firstChar);
    }
    this._startLetters = [...letterSet];
  }

  private createSpeechMap(entities: Speech[]): void {
    this._speechMap = {};
    for (const speech of entities) {
      let date = speech.startDate;
      if (!isDefined(this._speechMap[date])) {
        this._speechMap[date] = [];
      }
      this._speechMap[date].push(speech);
    }
  }

  public getSpeechMap(): Record<string, Speech[]> {
    return this._speechMap;
  }

  public hasMorePages(pageSize): boolean {
    return this._speechMap.length > pageSize;
  }

  public filterSpeeches(
    searchValue: string,
    filters: Record<string, string>,
    startLetter: string
  ): void {
    let filteredSpeeches: Speech[];
    searchValue = searchValue.trim().toLowerCase();
    if (searchValue.length > 0) {
      filteredSpeeches = this.applyFilters(filters, this._speeches);
      filteredSpeeches = filteredSpeeches.filter((e) => {
        if(e.subSpeeches.length > 0){
           for(const sub of e.subSpeeches){
             if(sub.hasSearchValue(searchValue)){
               return true;
             }
           }
        }
        return e.hasSearchValue(searchValue);
      });
      filteredSpeeches = this.applyFilters(filters, filteredSpeeches);
    } else {
      filteredSpeeches = this.applyFilters(filters, this._speeches);
    }
    this._speechesAfterSearchAndFilter = filteredSpeeches;
    if (startLetter !== "all" && startLetter !== "favorites") {
      filteredSpeeches = filteredSpeeches.filter((speech) => {
        let firstChar = speech.sortOrder[0]
          .toUpperCase()
          .normalize("NFD")
          .replace(/\p{Diacritic}/gu, "");
        if (!this._sortRegex.test(firstChar)) {
          firstChar = "#";
        }
        return firstChar === startLetter;
      });
    } else if (startLetter === "favorites") {
      let favoritesString = window.localStorage.getItem(
        "egcf_favorite_program_list"
      );
      if (isDefined(favoritesString)) {
        const favoritesIds: string[] = JSON.parse(favoritesString);
        filteredSpeeches = filteredSpeeches.filter((speech) => {
          return favoritesIds.includes(speech.id);
        });
      } else {
        filteredSpeeches = [];
      }
    }
    this.createSpeechMap(filteredSpeeches);
  }

  private applyFilters(
    filters: Record<string, string>,
    speeches: Speech[]
  ): Speech[] {
    if (isDefined(filters) && Object.entries(filters).length > 0) {
      for (const [key, value] of Object.entries(filters)) {
        speeches = speeches.filter((e) => {
          if (isDefined(e.themes)) {
            return e.themes.indexOf(value) > -1;
          }
          if(e.subSpeeches.length > 0){
            return this.applyFilters(filters,e.subSpeeches);
          }
          return false;
        });
      }
    }
    return speeches;
  }

  public hasEntries(): boolean {
    return JSON.stringify(this._speechMap) !== JSON.stringify({});
  }

  get startLetters(): string[] {
    return this._startLetters;
  }

  public letterExistsInCurrentList(letter: string): boolean {
    for (const speech of this._speechesAfterSearchAndFilter) {
      let firstChar = speech.sortOrder[0]
        .toUpperCase()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");
      if (!this._sortRegex.test(firstChar)) {
        firstChar = "#";
      }
      if (firstChar === letter) {
        return true;
      }
    }
    return false;
  }

  get speeches(): Speech[] {
    return this._speeches;
  }

  getSpeechesForSpeakerById(speakerId): Speech[] {
    if (!isDefined(this._speeches) || this._speeches.length === 0) {
      return [];
    }
    const result: Speech[] = [];
    result.push(...this.getSpeechesForSpeakerSub(speakerId, this._speeches));
    return result;
  }

  getSpeechesForSpeakerSub(speakerId: string, speeches: Speech[]): Speech[] {
    const result: Speech[] = [];
    for (const speech of speeches) {
      if (speech.persons.find((p) => p.id === speakerId) !== undefined) {
        result.push(speech);
      }
       if (speech.subSpeeches.length > 0) {
         result.push(
           ...this.getSpeechesForSpeakerSub(speakerId, speech.subSpeeches)
         );
       }
    }

    return result;
  }
}
